import type { FC } from "react";
import { memo } from "react";

import TelemetryButton from "@/design-system/telemetry-button";
import type { ICashbackLandingPageResources } from "@/src/cashback/LandingPage/CashbackLandingPage.Resources";
import {
    LoginModalView,
    useActiveLoginModalContext,
    useUserAuthenticationContext,
    useViewportInfoContext,
} from "@/src/contexts";
import ImageLoader from "@Components/ImageLoader";
import { getCdnImage, jumpToSectionId } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, type QueryXsMax, colors } from "../../../styles/tokens.stylex";

import * as css from "./CashbackTopSection.module.css";

export interface ICashbackTopSection {
    scrollToId: string;
    resources: ICashbackLandingPageResources["topSection"];
    version?: string;
}

const CashbackTopSection: FC<ICashbackTopSection> = memo((props) => {
    const { isMobile } = useViewportInfoContext();
    const { isAuthenticated } = useUserAuthenticationContext();
    const { setActiveLoginModal } = useActiveLoginModalContext();

    const openRegistration = () => setActiveLoginModal(LoginModalView.Registration);

    return (
        <div className={css.cashbackTopSectionComponent}>
            <div className={css.textContent}>
                <h1 className={css.subject} dangerouslySetInnerHTML={{ __html: props.resources.title }} />
                <span className={css.description}>{props.resources.subTitle}</span>
                {isAuthenticated ? null : (
                    <TelemetryButton
                        as="button"
                        variant="magentaRound"
                        size="L"
                        eventName="cshbck-topSectionRegister"
                        param1={props.version}
                        onClick={openRegistration}
                    >
                        {props.resources.registerText}
                    </TelemetryButton>
                )}
                {isMobile ? null : (
                    <TelemetryButton
                        as="button"
                        size="None"
                        variant="transparent"
                        eventName="cshbck-topSectionDetails"
                        param1={props.version}
                        onClick={() => jumpToSectionId(props.scrollToId, true)}
                        styleX={[styles.seemMoreBtn, isAuthenticated && styles.noMargin]}
                    >
                        {props.resources.anchorText}
                        <span {...stylex.props(styles.arrowDown)}>⬇</span>
                    </TelemetryButton>
                )}
            </div>
            <div {...stylex.props(styles.imageWrapper)}>
                <ImageLoader
                    src={getCdnImage(process.env.CB_TopSection)}
                    alt="Cashback - zwroty pieniędzy za zakupy"
                />
            </div>
        </div>
    );
});

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    arrowDown: {
        fontSize: "1.8rem",
        fontWeight: "bold",
        verticalAlign: "sub",
        marginLeft: ComposableSpacing.s1,
    },

    noMargin: {
        marginLeft: 0,
    },

    seemMoreBtn: {
        padding: 0,
        color: colors.fg_Accent,
        marginLeft: ComposableSpacing.s9,
    },

    imageWrapper: {
        height: { default: "auto", [smMax]: 380, [xsMax]: 284 },
        width: { default: "50%", [smMax]: "100%" },
    },
});

export default CashbackTopSection;
